import { parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * One way to write a customer's number, everywhere it is shown.
 *
 * ★ WHY THIS EXISTS: THE SAME PERSON LOOKED LIKE TWO.
 * A conversation's `customerPhone` is written by two different paths and they
 * disagree about the `+`. A thread WE start stores E.164 from the send service
 * (`+923001112233`); a thread the CUSTOMER starts is stored by the ingest path
 * through WhatsApp V1's `normalizePhone`, whose "add + if missing" step is
 * commented out — so it keeps the bare digits Meta sent (`923001112233`).
 *
 * Nothing is duplicated: the canonical room is keyed on `waId`, which is digits
 * only, so both paths land on one conversation. But the inbox showed one number
 * with a `+` and another without, side by side, which reads as two entries for
 * the same person — and made two genuinely different numbers look like the same
 * one written twice.
 *
 * ★ FIXED AT THE DISPLAY LAYER, DELIBERATELY. The writer that drops the `+` is
 * V1 code that WhatsApp V1 still depends on, and a stored value cannot be
 * rewritten for conversations that already exist. Formatting on the way out
 * makes every past and future row agree, whichever path wrote it.
 *
 * Output is Meta's own convention and the one people recognise:
 * `+92 314 973 0064`.
 */
export function formatWaNumber(value) {
	const raw = String(value ?? '').trim();
	if (!raw) return '';

	// A number the server masked for a viewer without "Lead contact details"
	// (`+971*********`) is shown exactly as sent: formatting would drop the
	// stars and print the country code as if it were the whole number.
	if (raw.includes('*')) return raw;

	// The stored value is E.164 with or without its `+`. Anything else — a name,
	// a partial — is returned untouched rather than mangled into a wrong number.
	const digits = raw.replace(/\D/g, '');
	if (!digits) return raw;
	// A leading zero means a LOCAL number, not E.164 — "0300 555 7777" is a
	// Pakistani number written the way Pakistanis write it, and prefixing a + to
	// it invents a country. Stored values are never in this shape; this is here
	// so a caller passing something else gets it back rather than a wrong number.
	if (digits.startsWith('0')) return raw;

	const parsed = parsePhoneNumberFromString(`+${digits}`);
	// `formatInternational` groups by the number's own country plan, so a UAE
	// number does not get Pakistani spacing.
	return parsed ? parsed.formatInternational() : `+${digits}`;
}

/**
 * What to call a conversation: the customer's name if we have one, otherwise
 * their number, formatted.
 *
 * ★ ONE ANSWER FOR THE LIST AND THE HEADER. They read the same fields but
 * rendered them differently, which is the other half of why the screen looked
 * inconsistent.
 */
export function conversationTitle(conversation) {
	const name = String(conversation?.customerName ?? '').trim();
	if (name) return name;
	return formatWaNumber(conversation?.customerPhone || conversation?.waId) || 'Unknown number';
}

/**
 * Does this conversation match what someone typed into the search box?
 *
 * ★ A SEARCH FOR A NUMBER MUST IGNORE PUNCTUATION. Someone pasting
 * `+92 314 973 0064` was matching nothing, because the stored value is
 * `923149730064` and the comparison was a plain substring. Digits are compared
 * to digits; everything else is compared as text.
 */
export function matchesSearch(conversation, query) {
	const needle = String(query ?? '').trim().toLowerCase();
	if (!needle) return true;

	if ((conversation?.customerName ?? '').toLowerCase().includes(needle)) return true;

	const needleDigits = needle.replace(/\D/g, '');
	if (!needleDigits) return false;

	const stored = `${conversation?.customerPhone ?? ''}${conversation?.waId ?? ''}`.replace(/\D/g, '');
	return stored.includes(needleDigits);
}
